import { ContractDamage, ContractHazard } from "@inrev/common";
import { FormMultiSelectDropdown } from "@portal/components/form/fields";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import type { z } from "zod";
import { contractDamageOptions, contractHazardOptions } from "../../constants";
import { formatEmpty } from "../../utils";
import { ViewSectionCardEditItem } from "./ViewSectionCardEditItem";

const CONTRACT_DAMAGES_TOOLTIP =
	"Damages are usually described in the contract but may only be described in the prime contract. <br/><br/>Note: AIA contracts usually describe damages in Section 4.5 (A101, A503).";
const CONTRACT_HAZARDS_TOOLTIP =
	"If these attributes are present, they will be described in the contract.";

const formatSelectionSummary = (
	selected: readonly string[] | undefined,
	options: { value: string; label: string }[],
	noneValue: string,
) => {
	const values = selected ?? [];
	if (values.includes(noneValue)) return "None";
	const labelByValue = new Map(options.map((o) => [o.value, o.label] as const));
	const labels = values
		.map((value) => labelByValue.get(value))
		.filter((label): label is string => Boolean(label));
	const remainingCount = Math.max(0, labels.length - 2);
	return formatEmpty(
		labels.slice(0, 2).join(", ") + (remainingCount > 0 ? `, + ${remainingCount} more` : ""),
	);
};

type ContractSelectionEditItemProps<TFieldValues extends FieldValues> = {
	formMethods: UseFormReturn<TFieldValues, any, any>;
	name: Path<TFieldValues>;
	editing: boolean;
	values: readonly string[] | undefined;
	schema?: z.ZodObject<any>;
	partialRequiresSubmit?: boolean;
};

export const ContractDamagesEditItem = <TFieldValues extends FieldValues>({
	values,
	...props
}: ContractSelectionEditItemProps<TFieldValues>) => (
	<ViewSectionCardEditItem
		label="Contract Damages"
		tooltip={CONTRACT_DAMAGES_TOOLTIP}
		viewComponent={formatSelectionSummary(values, contractDamageOptions, ContractDamage.none)}
		fieldSize="md"
		editComponent={
			<FormMultiSelectDropdown
				control={props.formMethods.control}
				name={props.name}
				options={contractDamageOptions}
				noneValue={ContractDamage.none}
				placeholder="Select damages"
				testId="contract-damages-multiselect"
				compact
			/>
		}
		{...props}
	/>
);

export const ContractHazardsEditItem = <TFieldValues extends FieldValues>({
	values,
	...props
}: ContractSelectionEditItemProps<TFieldValues>) => (
	<ViewSectionCardEditItem
		label="Contract Hazards"
		tooltip={CONTRACT_HAZARDS_TOOLTIP}
		viewComponent={formatSelectionSummary(values, contractHazardOptions, ContractHazard.none)}
		fieldSize="md"
		editComponent={
			<FormMultiSelectDropdown
				control={props.formMethods.control}
				name={props.name}
				options={contractHazardOptions}
				noneValue={ContractHazard.none}
				placeholder="Select hazards"
				testId="contract-hazards-multiselect"
				compact
			/>
		}
		{...props}
	/>
);
